import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ScheduleFreshnessBadge } from '../../../entities/schedule'
import { formatDayWindow } from '../model/formatDayWindow'
import { toIsoDate } from '../model/useWorkerCalendarDays'

interface Props {
  days: Date[]
  /** Today, so `Heute` can go quiet when the window already contains it. */
  todayIso: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  /** Freshness of the data below. Omit the whole group and no badge renders. */
  lastUpdatedAt?: number | null
  isSyncing?: boolean
  isDisconnected?: boolean
  onRefresh?: () => void
}

/**
 * Nav row, sized to survive a 320px viewport.
 *
 * Two rules do that. The controls never shrink and never wrap — a `Heute` that has folded onto a
 * second line has cost more height than the label it made room for. The date range takes whatever
 * is left and truncates, because it is the only part of the row that still reads correctly at half
 * width: `27.–29. …` is a range, a clipped arrow is nothing.
 *
 * The budget at 320px, with everything present: 16 padding + 56 `Heute` + 88 grouped arrows + 44
 * refresh + ~12 gaps = 216px, leaving about 104px for the range. Grouping the two arrows into one
 * bordered control rather than leaving them as separate 44px buttons with a gap between them is
 * what pays for the refresh button.
 *
 * Targets are 44px rather than the 32 they started at. `MIN_TAP_PX` is the floor the blocks on the
 * grid below keep for a thumb in a work glove; these are the controls a Handwerker hits most often,
 * so they get the full recommended size rather than the floor.
 */
export const CalendarHeader = ({
  days,
  todayIso,
  onPrev,
  onNext,
  onToday,
  lastUpdatedAt = null,
  isSyncing = false,
  isDisconnected = false,
  onRefresh,
}: Props) => {
  const label = formatDayWindow(days)
  // Nothing to go back to when today is already on screen — and the refresh button now owns the
  // job of re-fetching, which `Heute` used to be the only way to trigger.
  const showsToday = days.some((d) => toIsoDate(d) === todayIso)

  return (
    <div className="flex shrink-0 items-center gap-2 px-2 py-1 sm:px-3 sm:py-2 bg-white border-b border-gray-200 relative z-30">
      <button
        type="button"
        onClick={onToday}
        disabled={showsToday}
        className="shrink-0 h-11 px-2 sm:px-3 rounded-lg text-base font-semibold text-[#124093] hover:bg-gray-100 active:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
      >
        Heute
      </button>

      {/* One control, not two buttons that happen to be adjacent. */}
      <div className="flex shrink-0 items-center rounded-lg border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={onPrev}
          aria-label="Vorherige Tage"
          className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 active:bg-gray-100"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </button>
        <span className="w-px h-6 bg-gray-200" aria-hidden />
        <button
          type="button"
          onClick={onNext}
          aria-label="Nächste Tage"
          className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 active:bg-gray-100"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </button>
      </div>

      <span className="min-w-0 flex-1 truncate text-lg sm:text-xl font-semibold text-gray-800">
        {label}
      </span>

      {onRefresh && (
        <ScheduleFreshnessBadge
          lastUpdatedAt={lastUpdatedAt}
          isFetching={isSyncing}
          isDisconnected={isDisconnected}
          onRefresh={onRefresh}
        />
      )}
    </div>
  )
}
